/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import PlanModel, { PlanType } from 'cio/models/plan';
import MasqService from 'cio/services/masq';
import { Plans } from 'cio/utils/billing/plans';
import { inject as service } from '@ember/service';
import AccountService from 'cio/services/account';
import BillingService, { CDP_STARTER_PLAN_SLUG } from 'cio/services/billing';
import FeaturesService from 'ember-feature-flags/services/features';
import { tracked } from '@glimmer/tracking';
import ApiService from 'cio/services/api';
import { task } from 'ember-concurrency';
import { announceHydraNavigate } from '@customerio/hydra-nav-state/testing';
import { ProductApp } from 'cio/utils/product-app-info';
import SidebarService, { StaticSidebarId } from 'cio/services/sidebar';
import AnalyticsService from 'cio/services/analytics';

interface PlanInfoCtaButtonArgs {
  isPrimary: boolean;
  hasActiveSubscription: boolean;
  isPremiumTrial: boolean;
  planTemplate: PlanModel;
  essentialsCheckoutRoute: string;
  subscribedPlan: PlanType;
  showCheckout: boolean;
  onStartCheckout: () => void;
  trackCtaButtonClick: (value: string) => void;
}

export default class PlanInfoCtaButton extends Component<PlanInfoCtaButtonArgs> {
  @service masq!: MasqService;
  @service account!: AccountService;
  @service billing!: BillingService;
  @service features!: FeaturesService;
  @service api!: ApiService;
  @service sidebar!: SidebarService;
  @service analytics!: AnalyticsService;

  @tracked showDowngradeModal = false;
  @tracked letsTalkButtonClicked = '';
  @tracked shouldDisplayContractsSales = false;

  readonly plan = Plans;

  readonly sidebarId = StaticSidebarId.CurrentPlans;

  get cdpRequestAccessLink() {
    return `https://customer.io/cdp-early-access-request`;
  }

  get shouldDisplayBuyNow() {
    if (this.isEssentials === false) {
      return false;
    }

    if (this.args.isPremiumTrial === true) {
      return false;
    }

    if (this.billing.subscription?.plan.slug === CDP_STARTER_PLAN_SLUG) {
      return true;
    }

    return this.args.hasActiveSubscription === false;
  }

  get shouldDisplayCdpStarterCta() {
    return this.isCdpStarter && this.args.hasActiveSubscription === false && this.args.isPremiumTrial === false;
  }

  get cdpContactForm() {
    return (
      this.args.planTemplate.planType === Plans.CdpStarter.planType ||
      this.args.planTemplate.planType === Plans.CdpPremium.planType
    );
  }

  get isCdpStarter() {
    return this.args.planTemplate.planType === Plans.CdpStarter.planType;
  }

  get isEssentials() {
    return this.args.planTemplate.planType === Plans.Essentials.planType;
  }

  get shouldDisableCdpStarter() {
    return this.account.current?.isDataCenterEU === true;
  }

  get isTrial() {
    return this.args.isPremiumTrial || this.billing.billingInfo?.trial === true;
  }

  get isDataPipelinesActivated() {
    return this.account.current?.productActivations?.includes(ProductApp.DATA_PIPELINES) ?? false;
  }

  get buttonColor() {
    if (this.args.isPrimary) {
      if (this.isCdpStarter) {
        return 'fly-btn--aqua';
      }
      if (this.args.planTemplate.planType !== Plans.CdpPremium.planType) {
        return 'fly-btn--primary';
      }
    }
    return;
  }

  @action
  handleClickCdpStarterActivation() {
    if (this.shouldDisableCdpStarter) {
      return;
    }
    this.args.trackCtaButtonClick('Subscribe for free');

    this.showDowngradeModal = true;
  }

  subscribeTask = task({ drop: true }, async () => {
    let result = await this.billing.subscribeToPipelinesStarterTask.perform();

    this.showDowngradeModal = false;

    if (result !== true) {
      return;
    }

    this.analytics.track('plan_billing', 'start_cdp_starter_subscription_clicked', {
      'Account Id': this.account.current?.id,
    });

    if (!this.isDataPipelinesActivated) {
      announceHydraNavigate('/activate/pipelines');
    }
  });

  @action
  toggleSidebar(plan: PlanType) {
    if (!this.sidebar.isOpen) {
      this.args.trackCtaButtonClick("Let's Talk");
      this.letsTalkButtonClicked = plan;
    }
    this.sidebar.toggle(this.sidebarId);
  }
}
